/**
 * Types drafts générés Claude — alias FE typés sur le codegen OpenAPI.
 *
 * `frontend/src/types/api-generated.ts` est régénéré depuis les schémas Pydantic
 * du backend par `npm run gen:api` (cf `frontend/scripts/codegen-api-types.mjs`)
 * et n'est pas commité : `tsc` tourne toujours sur des types frais, en local
 * comme en CI — un schéma Pydantic qui bouge casse le typecheck FE, pas un diff.
 */

import type { components } from './api-generated'

export type DraftResponse = components['schemas']['DraftResponse']
export type DraftVersionResponse = components['schemas']['DraftVersionResponse']
export type GenerateDraftsRequest = components['schemas']['GenerateDraftsRequest']
export type RegenerateDraftRequest = components['schemas']['RegenerateDraftRequest']
export type ApproveDraftRequest = components['schemas']['ApproveDraftRequest']
export type DraftRephraseRequest = components['schemas']['DraftRephraseRequest']
export type DraftRephraseResponse = components['schemas']['DraftRephraseResponse']

// ── Cross-œuvres — page /drafts (S3 câblage) ────────────────────────────
export type DraftListResponse = components['schemas']['DraftListResponse']
export type DraftPendingCountResponse = components['schemas']['DraftPendingCountResponse']
export type DraftStatsResponse = components['schemas']['DraftStatsResponse']
export type DraftPlatformStats = components['schemas']['DraftPlatformStats']
export type DraftListSort = components['schemas']['DraftListSort']

export type Platform = components['schemas']['Platform']
export type DraftKind = components['schemas']['DraftKind']
export type DraftStatus = components['schemas']['DraftStatus']

// ── Lookups FR pour l'UI atelier ────────────────────────────────────────

export const PLATFORM_FR: Record<Platform, string> = {
  etsy: 'Etsy',
  pinterest: 'Pinterest',
  instagram: 'Instagram',
  saatchi: 'Saatchi',
  artfinder: 'Artfinder',
  tiktok: 'TikTok',
}

export const DRAFT_KIND_FR: Record<DraftKind, string> = {
  // Refonte drafts v12 — Etsy split en 2 sous-kinds. PHYSICAL couvre
  // print + original (1 listing Etsy physique), DIGITAL couvre les
  // téléchargements (1 listing Etsy digital download distinct).
  etsy_listing_physical: 'Annonce Etsy (physique)',
  etsy_listing_digital: 'Annonce Etsy (digital)',
  pinterest_pin: 'Pin Pinterest',
  instagram_carousel_en: 'Carrousel Instagram (EN)',
  instagram_carousel_bilingual: 'Carrousel Instagram (bilingue)',
  instagram_reel: 'Reel Instagram',
  saatchi_listing: 'Annonce Saatchi',
  artfinder_listing: 'Annonce Artfinder',
  tiktok_post: 'Post TikTok',
  tiktok_slideshow: 'Slideshow TikTok',
}

export const DRAFT_STATUS_FR: Record<DraftStatus, string> = {
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Rejeté',
}

// Plateforme parente d'un kind — pour résoudre `<PlatformDot>` à partir
// du kind. Aligné sur `DRAFT_KIND_TO_PLATFORM` côté BE.
export const DRAFT_KIND_TO_PLATFORM: Record<DraftKind, Platform> = {
  etsy_listing_physical: 'etsy',
  etsy_listing_digital: 'etsy',
  pinterest_pin: 'pinterest',
  instagram_carousel_en: 'instagram',
  instagram_carousel_bilingual: 'instagram',
  instagram_reel: 'instagram',
  saatchi_listing: 'saatchi',
  artfinder_listing: 'artfinder',
  tiktok_post: 'tiktok',
  tiktok_slideshow: 'tiktok',
}

// Tri de la page /drafts (cross-œuvres). FR pour le <Select>.
export const DRAFT_LIST_SORT_FR: Record<DraftListSort, string> = {
  recent: 'Récents',
  oldest: 'Plus anciens',
  work_title: 'Par œuvre',
}

export const DRAFT_LIST_SORT_OPTIONS: DraftListSort[] = ['recent', 'oldest', 'work_title']

export const PLATFORM_OPTIONS: Platform[] = [
  'etsy',
  'pinterest',
  'instagram',
  'saatchi',
  'artfinder',
  'tiktok',
]

export const DRAFT_STATUS_OPTIONS: DraftStatus[] = ['pending', 'approved', 'rejected']

export const DRAFT_KIND_OPTIONS: DraftKind[] = [
  'etsy_listing_physical',
  'etsy_listing_digital',
  'pinterest_pin',
  'instagram_carousel_en',
  'instagram_carousel_bilingual',
  'saatchi_listing',
  'artfinder_listing',
  'tiktok_post',
]

/**
 * DraftKind groupés par plateforme — pour le `<Select>` filtre kind de la
 * page `/drafts` (refonte v12 PR-2). Permet de rendre les 2 sous-kinds Etsy
 * comme un optgroup "Etsy" plutôt que 2 entrées plates noyées dans la liste.
 */
export const DRAFT_KIND_OPTIONS_GROUPED: ReadonlyArray<{
  platform: Platform
  kinds: ReadonlyArray<DraftKind>
}> = [
  { platform: 'etsy', kinds: ['etsy_listing_physical', 'etsy_listing_digital'] },
  { platform: 'pinterest', kinds: ['pinterest_pin'] },
  {
    platform: 'instagram',
    kinds: ['instagram_carousel_bilingual', 'instagram_carousel_en', 'instagram_reel'],
  },
  { platform: 'saatchi', kinds: ['saatchi_listing'] },
  { platform: 'artfinder', kinds: ['artfinder_listing'] },
  { platform: 'tiktok', kinds: ['tiktok_post', 'tiktok_slideshow'] },
]

/**
 * Seuil minimum de drafts par plateforme avant d'afficher les KPIs Tier 4.
 *
 * En dessous, edit_rate / avg_edit_distance sont statistiquement trompeurs
 * (10 drafts = signal robuste, 1-2 = bruit). À ré-évaluer quand
 * `edit_distance_to_published` sera peuplé en S5c retrofit.
 */
export const MIN_DRAFTS_FOR_TIER4_KPI = 10
